import { useQuery } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { useSearch } from '@tanstack/react-router';
import { orderBy } from 'lodash';
import type React from 'react';
import { useId } from 'react';
import allViews from 'src/api/viewModels/allViews';
import {
  outlinedInputStyles,
  styles,
} from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopFilterNavBar/AllCategoriesDropdown/AllCategoriesDropdownStyles';
import { cleanPayload } from 'src/helpers/common';
import { matchesFieldScope } from 'src/helpers/scope';
import type { BaseSearchParams } from 'src/utils/searchSchemas';

interface FieldNode {
  id: string;
  slug: string;
  name: string;
  scope?: string[];
}

interface ViewFieldNode {
  field: FieldNode;
  fieldLabel: string;
  sequence: number;
  id: string;
}

interface ViewFieldEdge {
  node: ViewFieldNode;
}

interface ViewFieldSet {
  edges: ViewFieldEdge[];
}

interface ViewModel {
  viewfieldSet: ViewFieldSet;
}

interface AllCategoriesDropdownProps {
  category: string;
  handleCategory: (id: string, name: string, label: string) => void;
  categoryExclude: string[];
}

export default function AllCategoriesDropdown({
  category,
  handleCategory,
  categoryExclude,
}: AllCategoriesDropdownProps): React.ReactElement | null {
  const classes = styles();
  const outlinedInputClasses = outlinedInputStyles();
  const search = useSearch({ strict: false }) as BaseSearchParams;
  const selectId = useId();

  const { data, loading, error } = useQuery(allViews, {
    variables: {
      viewName: 'Library',
      componentName: 'FilterBarFirstDropdown',
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
  });

  if (loading) return <CircularProgress color="secondary" />;
  if (error) return <div>Error: {error.message}</div>;

  const cleanedData = cleanPayload(data) as ViewModel[];
  const viewModel = cleanedData[0];

  const { edges: categories } = viewModel.viewfieldSet;

  const handleChange = (e: SelectChangeEvent<string>): void => {
    if (e.target.value === '') return;
    const foundCategory = categories.find((item) => item.node.field.id === e.target.value);
    if (foundCategory) {
      handleCategory(e.target.value, foundCategory.node.field.slug, foundCategory.node.field.name);
    }
  };

  if (categories) {
    return (
      <div>
        <FormControl className={classes.formControl} size="small">
          <Select
            sx={{
              color: 'text.primary',
              backgroundColor: 'background.paper',
              borderRadius: '7px',
            }}
            className={classes.selectBorder}
            displayEmpty
            label="Category"
            value={category}
            onChange={handleChange}
            id={selectId}
            data-testid="cyp-top-nav-filter-select"
            input={<OutlinedInput classes={outlinedInputClasses} />}
          >
            <MenuItem value="">Filters</MenuItem>
            {orderBy(categories, ['node.sequence'], ['asc'])
              .filter(
                (obj) => !categoryExclude.some((word) => obj.node.field?.slug?.toLowerCase() === word.toLowerCase()),
              )
              .filter((obj) => matchesFieldScope(obj.node.field.scope, search.scope))
              .map((item) => {
                return (
                  <MenuItem
                    id={`cyp-top-nav-item-${item.node.field.name.toLowerCase().split(' ').join('-')}`}
                    key={item.node.sequence}
                    value={item.node.field.id}
                  >
                    {item.node.field.name}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </div>
    );
  }
  return null;
}
